"use client";

import type { SectionMeta } from "@/entities/section";
import type { TeamMember } from "@/entities/team";
import type { ContactChannel } from "@/entities/contacts";
import { useState, useRef, useEffect, memo } from "react";
import {
  PageFooterSection,
  type TeamMember as PFTeamMember,
  type ContactLink,
} from "./PageFooterSection";
import { motion } from "motion/react";
import type { Course, CourseDifficulty } from "@/entities/course";
import { getDifficultyLabel } from "@/entities/course";
import { AccentStar } from "@/shared/ui/accent-star";
import schoolPaths from "@/shared/ui/icons/svg-xgh9f7apn2";
import { CourseCard } from "./course-card";
import { Phone, Mail, Send, MessageCircle } from "lucide-react";

/* ── School animated hero logo (char-appear, same technique as HelpPage HeroSection) ── */
type LogoAnimState = "idle" | "animating" | "done";
const SCHOOL_SESSION_KEY = "practicum_school_intro_v1";
const _S  = 0.13;           // ms per character
const _WP = 0.25;           // word pause before "school"
const _SS = 9 * _S + _WP;  // school section start time

function _cStyle(d: number, s: LogoAnimState): React.CSSProperties {
  if (s === "done") return { opacity: 1 };
  if (s === "idle") return { opacity: 0 };
  return { opacity: 0, animation: `char-appear 0.001s step-start ${d.toFixed(3)}s forwards` };
}

const SCHOOL_CHARS: Array<{ d: string; delay: number; clip?: boolean }> = [
  { d: schoolPaths.p17f050c0, delay: 0 * _S },
  { d: schoolPaths.p1919af00, delay: 1 * _S },
  { d: schoolPaths.p20466f00, delay: 2 * _S },
  { d: schoolPaths.p1ad6b600, delay: 3 * _S },
  { d: schoolPaths.p241d8f80, delay: 4 * _S },
  { d: schoolPaths.p2af9e00,  delay: 5 * _S },
  { d: schoolPaths.p3f856870, delay: 6 * _S },
  { d: schoolPaths.pfa63000,  delay: 7 * _S },
  { d: schoolPaths.p993c880,  delay: 8 * _S, clip: true },
  { d: schoolPaths.p38463200, delay: _SS },
  { d: schoolPaths.p2d15ba00, delay: _SS + 1 * _S },
  { d: schoolPaths.p1fbc4900, delay: _SS + 2 * _S },
  { d: schoolPaths.p1c653f0,  delay: _SS + 3 * _S },
  { d: schoolPaths.p2c3c4080, delay: _SS + 4 * _S },
  { d: schoolPaths.p622c600,  delay: _SS + 5 * _S },
];

const SCHOOL_STAR_DELAY = 8 * _S + 0.25; // bounce after 'A' body path

function AnimatedSchoolLogo({ state }: { state: LogoAnimState }) {
  const starBase: React.CSSProperties = { transformBox: "fill-box", transformOrigin: "center" };
  const starStyle: React.CSSProperties = state === "done"
    ? { ...starBase, opacity: 1, transform: "scale(1)" }
    : state === "animating"
      ? { ...starBase, opacity: 0, animation: `star-bounce-zoom 0.6s cubic-bezier(0.34,1.56,0.64,1) ${SCHOOL_STAR_DELAY.toFixed(3)}s forwards` }
      : { ...starBase, opacity: 0, transform: "scale(0)" };
  return (
    <svg viewBox="0 0 282.876 60.366" fill="none" className="w-full h-full max-w-[min(800px,90vw)]" aria-label="Practicum School">
      {SCHOOL_CHARS.map((c, i) => (
        <path
          key={i}
          d={c.d}
          fill="currentColor"
          clipRule={c.clip ? "evenodd" : undefined}
          fillRule={c.clip ? "evenodd" : undefined}
          style={_cStyle(c.delay, state)}
        />
      ))}
      <g style={starStyle}>
        <path d={schoolPaths.p8275a00} fill="#1192E6" clipRule="evenodd" fillRule="evenodd" />
      </g>
    </svg>
  );
}

function SchoolLogoHero() {
  const [state, setState] = useState<LogoAnimState>("idle");
  const elRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const done = typeof sessionStorage !== "undefined" && sessionStorage.getItem(SCHOOL_SESSION_KEY) === "1";
    if (done) { setState("done"); return; }
    const el = elRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        sessionStorage.setItem(SCHOOL_SESSION_KEY, "1");
        setState("animating");
        io.disconnect();
      }
    }, { threshold: 0.1 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={elRef} className="flex items-center justify-center flex-1 min-h-0">
      <AnimatedSchoolLogo state={state} />
    </div>
  );
}

const ICON_BY_KIND: Record<ContactChannel["kind"], React.ReactNode> = {
  phone: <Phone className="w-5 h-5" />,
  email: <Mail className="w-5 h-5" />,
  telegram: <Send className="w-5 h-5" />,
  whatsapp: <MessageCircle className="w-5 h-5" />,
  address: <Mail className="w-5 h-5" />,
};

// Animated SVG illustration component
const AnimatedIllustration = memo(function AnimatedIllustration({
  index,
}: {
  index: number;
}) {
  const [isHovered, setIsHovered] = useState(false);

  const patterns = [
    // Pattern 0 - Flowing curves
    () => (
      <svg viewBox="0 0 400 200" className="w-full h-full">
        <motion.path
          d="M0,100 Q100,20 200,100 T400,100 L400,200 L0,200 Z"
          fill="var(--school)"
          animate={
            isHovered
              ? {
                  x: [0, 15, -10, 20, 0],
                  y: [0, -8, 5, -12, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 3.2 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.path
          d="M0,120 Q150,60 300,120 T600,120 L600,200 L0,200 Z"
          fill="var(--base)"
          animate={
            isHovered
              ? {
                  x: [0, -18, 12, -15, 0],
                  y: [0, 10, -6, 8, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 3.8 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.circle
          cx="80"
          cy="80"
          r="40"
          fill="var(--help)"
          animate={
            isHovered
              ? {
                  x: [0, 25, -15, 30, 0],
                  y: [0, -20, 12, -25, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 2.8 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.circle
          cx="320"
          cy="130"
          r="30"
          fill="var(--media)"
          animate={
            isHovered
              ? {
                  x: [0, -22, 18, -28, 0],
                  y: [0, 15, -10, 20, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 3.4 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
      </svg>
    ),
    // Pattern 1 - Geometric shapes
    () => (
      <svg viewBox="0 0 400 200" className="w-full h-full">
        <motion.rect
          x="20"
          y="40"
          width="100"
          height="100"
          fill="var(--school)"
          rx="12"
          animate={
            isHovered
              ? {
                  x: [0, 28, -18, 32, 0],
                  y: [0, -15, 10, -20, 0],
                  rotate: [0, 5, -3, 8, 0],
                }
              : { x: 0, y: 0, rotate: 0 }
          }
          transition={{
            duration: isHovered ? 3.6 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.rect
          x="150"
          y="80"
          width="80"
          height="80"
          fill="var(--base)"
          rx="8"
          animate={
            isHovered
              ? {
                  x: [0, -20, 15, -25, 0],
                  y: [0, 22, -12, 18, 0],
                  rotate: [0, -6, 4, -8, 0],
                }
              : { x: 0, y: 0, rotate: 0 }
          }
          transition={{
            duration: isHovered ? 4.0 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.circle
          cx="340"
          cy="100"
          r="50"
          fill="var(--help)"
          animate={
            isHovered
              ? {
                  x: [0, 30, -20, 35, 0],
                  y: [0, -28, 18, -32, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 2.6 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.path
          d="M250,50 L300,150 L200,150 Z"
          fill="var(--media)"
          animate={
            isHovered
              ? {
                  x: [0, -25, 18, -30, 0],
                  y: [0, 12, -8, 15, 0],
                  rotate: [0, 8, -5, 10, 0],
                }
              : { x: 0, y: 0, rotate: 0 }
          }
          transition={{
            duration: isHovered ? 3.2 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
      </svg>
    ),
    // Pattern 2 - Organic waves
    () => (
      <svg viewBox="0 0 400 200" className="w-full h-full">
        <motion.path
          d="M0,80 C100,40 200,120 400,80 L400,200 L0,200 Z"
          fill="var(--school)"
          animate={
            isHovered
              ? {
                  x: [0, 22, -15, 25, 0],
                  y: [0, -18, 10, -22, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 3.8 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.path
          d="M0,140 C120,100 280,160 400,130 L400,200 L0,200 Z"
          fill="var(--base)"
          animate={
            isHovered
              ? {
                  x: [0, -25, 18, -28, 0],
                  y: [0, 12, -8, 15, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 4.2 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.ellipse
          cx="200"
          cy="60"
          rx="60"
          ry="40"
          fill="var(--help)"
          animate={
            isHovered
              ? {
                  x: [0, 28, -20, 32, 0],
                  y: [0, -25, 15, -30, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 2.8 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
      </svg>
    ),
    // Pattern 3 - Scattered circles
    () => (
      <svg viewBox="0 0 400 200" className="w-full h-full">
        <motion.circle
          cx="60"
          cy="60"
          r="35"
          fill="var(--school)"
          animate={
            isHovered
              ? {
                  x: [0, 20, -15, 25, 0],
                  y: [0, -18, 12, -22, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 3.0 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.circle
          cx="180"
          cy="120"
          r="45"
          fill="var(--base)"
          animate={
            isHovered
              ? {
                  x: [0, -28, 20, -32, 0],
                  y: [0, 22, -15, 25, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 3.4 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.circle
          cx="320"
          cy="70"
          r="40"
          fill="var(--help)"
          animate={
            isHovered
              ? {
                  x: [0, 32, -22, 35, 0],
                  y: [0, -28, 18, -32, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 2.6 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.circle
          cx="250"
          cy="150"
          r="30"
          fill="var(--media)"
          animate={
            isHovered
              ? {
                  x: [0, -18, 12, -22, 0],
                  y: [0, 15, -10, 18, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 3.8 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.circle
          cx="120"
          cy="140"
          r="25"
          fill="var(--school)"
          animate={
            isHovered
              ? {
                  x: [0, 22, -15, 28, 0],
                  y: [0, -12, 8, -15, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 4.0 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
      </svg>
    ),
    // Pattern 4 - Linear grid
    () => (
      <svg viewBox="0 0 400 200" className="w-full h-full">
        <motion.line
          x1="0"
          y1="50"
          x2="400"
          y2="50"
          stroke="var(--school)"
          strokeWidth="2"
          animate={
            isHovered
              ? {
                  x: [0, 35, -25, 40, 0],
                  y: [0, 8, -5, 10, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 3.2 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.line
          x1="0"
          y1="100"
          x2="400"
          y2="100"
          stroke="var(--base)"
          strokeWidth="3"
          animate={
            isHovered
              ? {
                  x: [0, -30, 22, -35, 0],
                  y: [0, -6, 4, -8, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 3.6 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.line
          x1="0"
          y1="150"
          x2="400"
          y2="150"
          stroke="var(--school)"
          strokeWidth="2"
          animate={
            isHovered
              ? {
                  x: [0, 32, -20, 38, 0],
                  y: [0, 10, -6, 12, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 4.0 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.rect
          x="50"
          y="60"
          width="80"
          height="80"
          fill="var(--help)"
          rx="8"
          animate={
            isHovered
              ? {
                  x: [0, -22, 15, -28, 0],
                  y: [0, 18, -12, 22, 0],
                  rotate: [0, -5, 3, -8, 0],
                }
              : { x: 0, y: 0, rotate: 0 }
          }
          transition={{
            duration: isHovered ? 3.4 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.rect
          x="270"
          y="40"
          width="60"
          height="100"
          fill="var(--media)"
          rx="6"
          animate={
            isHovered
              ? {
                  x: [0, 25, -18, 30, 0],
                  y: [0, -20, 12, -25, 0],
                  rotate: [0, 6, -4, 8, 0],
                }
              : { x: 0, y: 0, rotate: 0 }
          }
          transition={{
            duration: isHovered ? 2.8 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
      </svg>
    ),
    // Pattern 5 - Diagonal composition
    () => (
      <svg viewBox="0 0 400 200" className="w-full h-full">
        <motion.path
          d="M0,0 L200,200 L0,200 Z"
          fill="var(--school)"
          animate={
            isHovered
              ? {
                  x: [0, -25, 18, -30, 0],
                  y: [0, 12, -8, 15, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 3.8 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.path
          d="M200,0 L400,200 L200,200 Z"
          fill="var(--base)"
          animate={
            isHovered
              ? {
                  x: [0, 25, -18, 30, 0],
                  y: [0, -12, 8, -15, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 4.2 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.circle
          cx="100"
          cy="100"
          r="40"
          fill="var(--help)"
          animate={
            isHovered
              ? {
                  x: [0, 32, -22, 38, 0],
                  y: [0, -28, 18, -35, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 2.6 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.circle
          cx="300"
          cy="100"
          r="35"
          fill="var(--media)"
          animate={
            isHovered
              ? {
                  x: [0, -30, 22, -35, 0],
                  y: [0, 25, -15, 28, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 3.0 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
      </svg>
    ),
    // Pattern 6 - Flowing ribbons
    () => (
      <svg viewBox="0 0 400 200" className="w-full h-full">
        <motion.path
          d="M0,60 Q100,20 200,60 T400,60"
          stroke="var(--school)"
          strokeWidth="20"
          fill="none"
          animate={
            isHovered
              ? {
                  x: [0, 25, -18, 30, 0],
                  y: [0, -20, 12, -25, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 3.2 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.path
          d="M0,120 Q150,80 300,120 T600,120"
          stroke="var(--base)"
          strokeWidth="25"
          fill="none"
          animate={
            isHovered
              ? {
                  x: [0, -28, 20, -32, 0],
                  y: [0, 15, -10, 18, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 3.6 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.path
          d="M0,160 Q80,140 160,160 T320,160"
          stroke="var(--media)"
          strokeWidth="15"
          fill="none"
          animate={
            isHovered
              ? {
                  x: [0, 32, -22, 35, 0],
                  y: [0, -22, 15, -28, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 2.8 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
      </svg>
    ),
    // Pattern 7 - Minimal dots
    () => (
      <svg viewBox="0 0 400 200" className="w-full h-full">
        <motion.circle
          cx="80"
          cy="100"
          r="50"
          fill="var(--school)"
          animate={
            isHovered
              ? {
                  x: [0, -28, 20, -32, 0],
                  y: [0, 22, -15, 25, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 3.4 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.circle
          cx="200"
          cy="80"
          r="60"
          fill="var(--base)"
          animate={
            isHovered
              ? {
                  x: [0, 25, -18, 30, 0],
                  y: [0, -28, 18, -32, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 2.8 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.circle
          cx="320"
          cy="120"
          r="45"
          fill="var(--help)"
          animate={
            isHovered
              ? {
                  x: [0, 32, -22, 35, 0],
                  y: [0, -20, 15, -25, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 3.0 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
        <motion.circle
          cx="140"
          cy="140"
          r="30"
          fill="var(--media)"
          animate={
            isHovered
              ? {
                  x: [0, -25, 18, -28, 0],
                  y: [0, 18, -12, 22, 0],
                }
              : { x: 0, y: 0 }
          }
          transition={{
            duration: isHovered ? 3.6 : 0.4,
            repeat: isHovered ? Infinity : 0,
            ease: isHovered ? "easeInOut" : "easeOut",
          }}
        />
      </svg>
    ),
  ];

  const Pattern = patterns[index % patterns.length];
  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="w-full h-full"
    >
      {Pattern()}
    </div>
  );
});

function DifficultyStars({ level }: { level: CourseDifficulty }) {
  return (
    <div className="flex items-center gap-fluid-sm">
      <div className="flex items-center gap-fluid-xs">
        {[1, 2, 3, 4, 5].map((star) => (
          <AccentStar
            key={star}
            color={star <= level ? "var(--media)" : "var(--muted)"}
            size={16}
            style={{ opacity: star <= level ? 1 : 0.6 }}
          />
        ))}
      </div>
      <span className="text-fluid-xs tracking-wide font-medium font-content text-media">
        {getDifficultyLabel(level)}
      </span>
    </div>
  );
}

function ProgressBar({ progress }: { progress: number }) {
  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-fluid-xs">
        <span className="text-fluid-xs tracking-wide font-content text-muted">
          Прогресс
        </span>
        <span className="text-fluid-xs tabular-nums font-content text-foreground">
          {progress}%
        </span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden bg-border">
        <div
          className="h-full rounded-full transition-all duration-500 bg-school"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

interface SchoolContentProps {
  courses: Course[];
  isLoggedIn: boolean;
  section: SectionMeta;
  teamMembers: TeamMember[];
  contactChannels: ContactChannel[];
  onLoginClick?: () => void;
  onProfileClick?: () => void;
}

export function SchoolContent({
  courses,
  isLoggedIn,
  section,
  teamMembers,
  contactChannels,
  onLoginClick,
  onProfileClick,
}: SchoolContentProps) {
  const teamMembersUI: PFTeamMember[] = teamMembers.map((m) => ({
    name: m.name,
    role: m.role,
    initials: m.initials,
    photo: m.photo,
  }));
  const contactLinks: ContactLink[] = contactChannels.map((c) => ({
    icon: ICON_BY_KIND[c.kind],
    title: c.title,
    value: c.value,
    href: c.href,
  }));
  const [filter, setFilter] = useState<string | null>(null);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [favorites, setFavorites] = useState<Set<string>>(
    new Set(courses.filter((c) => c.favorite).map((c) => c.id)),
  );
  const [expandedCourse, setExpandedCourse] = useState<string | null>(null);
  const [savedScrollPosition, setSavedScrollPosition] = useState<number>(0);

  const toggleFavorite = (courseId: string) => {
    setFavorites((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(courseId)) {
        newSet.delete(courseId);
      } else {
        newSet.add(courseId);
      }
      return newSet;
    });
  };

  const handleToggleExpand = (courseId: string) => {
    if (expandedCourse === courseId) {
      // Закрытие текущей карточки
      setExpandedCourse(null);
      window.scrollTo({ top: savedScrollPosition, behavior: "smooth" });
    } else if (expandedCourse) {
      // Переключение с одной раскрытой на другую —
      // НЕ перезаписываем savedScrollPosition (храним оригинальную позицию до любого expand)
      setExpandedCourse(courseId);

      // Больший delay: старая карточка коллапсирует + swap меняется + новая раскрывается
      setTimeout(() => {
        const cardElement = document.getElementById(`course-card-${courseId}`);
        if (cardElement) {
          const topOffset = 64 + 24;
          const elementPosition = cardElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - topOffset;
          window.scrollTo({ top: offsetPosition, behavior: "smooth" });
        }
      }, 500);
    } else {
      // Первое раскрытие — сохраняем позицию скролла
      setSavedScrollPosition(window.scrollY);
      setExpandedCourse(courseId);

      setTimeout(() => {
        const cardElement = document.getElementById(`course-card-${courseId}`);
        if (cardElement) {
          const topOffset = 64 + 24;
          const elementPosition = cardElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - topOffset;
          window.scrollTo({ top: offsetPosition, behavior: "smooth" });
        }
      }, 400);
    }
  };

  const allTags = [
    "Все",
    ...Array.from(new Set(courses.flatMap((c) => c.tags))),
  ];

  const filtered = courses.filter((course) => {
    const matchesTag =
      !filter || filter === "Все" || course.tags.includes(filter);
    const matchesFavorite = !showFavoritesOnly || course.favorite;
    return matchesTag && matchesFavorite;
  });

  // Рекомендации на основе прогресса
  const inProgress = courses.filter((c) => c.progress > 0 && c.progress < 100);
  const recommended = courses
    .filter((c) => c.progress === 0 && !c.favorite)
    .slice(0, 3);

  return (
    <div className="flex flex-col gap-fluid-2xl">
      {/* Hero */}
      <section
        className="flex flex-col"
        style={{
          minHeight:
            "clamp(480px, calc(100svh - 2 * (var(--ring-nav-height, 80px) + var(--ring-nav-padding, 12px) * 2)), 100svh)",
        }}
      >
        <div className="flex flex-col gap-fluid-lg h-full p-fluid-xl flex-1 rounded-[var(--fluid-radius-lg)] border border-[var(--school)] bg-white overflow-hidden">
          {/* Логотип — char-appear animation, once per session */}
          <SchoolLogoHero />

          {/* overflow-hidden скрывает блок до момента выдвижения */}
          <div className="overflow-hidden shrink-0">
            <div className="text-left pt-fluid-lg border-t border-[var(--school)]/20">
              <h1 className="font-content font-semibold text-fluid-4xl leading-tight text-foreground block select-none">
                Формирование <span className="text-school">клинического</span> мышления
              </h1>
              <p className="hidden font-content font-light text-fluid-xl leading-snug text-[var(--foreground)] mt-fluid-sm block select-none max-w-[65ch]">
                Доказательные методы, критическое мышление, практические навыки.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content section */}
      {/* <div className="py-fluid-xl">
        {isLoggedIn && inProgress.length > 0 && (
          <div className="mb-fluid-xl">
            <h2 className="text-fluid-2xl tracking-tight mb-fluid-md font-heading">
              Продолжить обучение
            </h2>
            <div className="grid lg:grid-cols-2 gap-fluid-md">
              {inProgress.map((course) => (
                <Link
                  key={course.id}
                  href={`/school/${course.id}`}
                  className="text-left p-fluid-md rounded-xl border-2 transition-all hover:border-opacity-100 border-school/40"
                >
                  <div className="flex items-start justify-between mb-fluid-xs">
                    <h3 className="text-fluid-xl font-heading text-foreground">
                      {course.title}
                    </h3>
                    {course.favorite && (
                      <AccentStar color="var(--school)" size={12} />
                    )}
                  </div>
                  <ProgressBar progress={course.progress} />
                </Link>
              ))}
            </div>
          </div>
        )}

        {isLoggedIn && recommended.length > 0 && (
          <div className="mb-fluid-xl">
            <h2 className="text-fluid-2xl tracking-tight mb-fluid-md font-heading">
              Рекомендации для вас
            </h2>
            <div className="grid lg:grid-cols-3 gap-fluid-md">
              {recommended.map((course) => (
                <div
                  key={course.id}
                  className="p-fluid-md rounded-xl border-2 border-border"
                >
                  <DifficultyStars
                    level={course.difficulty}
                    difficultyLabels={difficultyLabels}
                  />
                  <h3 className="text-fluid-lg mt-fluid-xs mb-fluid-xs font-heading">
                    {course.title}
                  </h3>
                  <p className="text-fluid-sm mb-fluid-sm font-content text-muted">
                    {course.subtitle}
                  </p>
                  <div className="flex flex-wrap gap-fluid-xs">
                    {course.tags.slice(0, 2).map((tag: string) => (
                      <span
                        key={tag}
                        className="text-fluid-xs tracking-wide px-2 py-1 rounded-full font-content text-school bg-school/15"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-fluid-md">
          <div className="flex flex-wrap gap-fluid-sm">
            {allTags.map((tag) => (
              <button
                key={tag}
                onClick={() => setFilter(tag === "Все" ? null : tag)}
                data-active={filter === tag || (tag === "Все" && !filter)}
                className="px-4 py-2 rounded-full text-fluid-xs tracking-wide transition-all font-content bg-transparent data-[active=true]:bg-school text-foreground data-[active=true]:text-white border-2 border-border data-[active=true]:border-school"
              >
                {tag}
              </button>
            ))}
          </div>

          {isLoggedIn && (
            <button
              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              data-active={showFavoritesOnly}
              className="flex items-center gap-fluid-xs px-4 py-2 rounded-full text-fluid-xs tracking-wide transition-all font-content bg-transparent data-[active=true]:bg-school text-foreground data-[active=true]:text-white border-2 border-border data-[active=true]:border-school"
            >
              <AccentStar
                color={showFavoritesOnly ? "#fff" : "var(--school)"}
                size={10}
              />
              Избранное
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-fluid-lg">
          {(() => {
            const expandedIdx = expandedCourse
              ? filtered.findIndex((c) => c.id === expandedCourse)
              : -1;

            let ordered: typeof filtered;

            if (expandedIdx === -1) {
              ordered = filtered;
            } else {
              const pairStart = expandedIdx - (expandedIdx % 2);
              const before = filtered.slice(0, pairStart);
              const expandedCard = filtered[expandedIdx];
              const neighborIdx =
                expandedIdx % 2 === 0 ? expandedIdx + 1 : expandedIdx - 1;
              const neighbor =
                neighborIdx >= 0 && neighborIdx < filtered.length
                  ? filtered[neighborIdx]
                  : null;
              const afterPair = filtered.slice(pairStart + 2);
              ordered = [
                ...before,
                expandedCard,
                ...(neighbor ? [neighbor] : []),
                ...afterPair,
              ];
            }

            return ordered.map((course) => {
              const isExpanded = expandedCourse === course.id;
              return (
                <div
                  key={course.id}
                  style={isExpanded ? { gridColumn: "1 / -1" } : undefined}
                >
                  <CourseCard
                    course={course}
                    favorites={favorites}
                    toggleFavorite={toggleFavorite}
                    isLoggedIn={isLoggedIn}
                    isExpanded={isExpanded}
                    onToggleExpand={() => handleToggleExpand(course.id)}
                    illustrationIndex={courses.indexOf(course)}
                    AnimatedIllustration={AnimatedIllustration}
                    DifficultyStars={(props: { level: number }) => (
                      <DifficultyStars
                        {...props}
                        difficultyLabels={difficultyLabels}
                      />
                    )}
                    ProgressBar={ProgressBar}
                  />
                </div>
              );
            });
          })()}
        </div>
      </div> */}

      <PageFooterSection
        teamMembers={teamMembersUI}
        teamLabel="Команда Practicum School"
        contactLinks={contactLinks}
        accentColor="var(--school)"
      />
    </div>
  );
}

export function CoursePage({
  courseId,
  courses,
  onBack,
  isLoggedIn,
}: {
  courseId: string;
  courses: Course[];
  onBack: () => void;
  isLoggedIn: boolean;
}) {
  const course = courses.find((c) => c.id === courseId);

  if (!course) {
    return (
      <div className="flex items-center justify-center px-10">
        <p className="font-content">Курс не найден</p>
      </div>
    );
  }

  return (
    <div className="px-fluid-lg py-fluid-xl">
      {/* Back button */}
      <button
        onClick={onBack}
        className="mb-fluid-md text-fluid-base transition-colors flex items-center gap-fluid-xs"
        style={{ fontFamily: "var(--font-content)", color: "var(--school)" }}
      >
        <span>←</span> Все курсы
      </button>

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <DifficultyStars level={course.difficulty} />
        <h1 className="text-fluid-4xl tracking-tight mt-fluid-sm mb-fluid-sm font-heading leading-tight">
          {course.title}
        </h1>
        <p className="text-fluid-xl leading-relaxed mb-fluid-md font-content text-muted">
          {course.subtitle}
        </p>

        {/* Meta */}
        <div className="flex items-center gap-fluid-md mb-fluid-lg">
          <span className="text-fluid-base font-content text-foreground">
            {course.duration}
          </span>
          <span className="text-fluid-base font-content text-foreground">
            {course.modules} модулей
          </span>
        </div>

        {/* Progress (если залогинен) */}
        {isLoggedIn && course.progress > 0 && (
          <div className="mb-fluid-lg">
            <ProgressBar progress={course.progress} />
          </div>
        )}

        {/* Description */}
        <p className="text-fluid-lg leading-relaxed mb-fluid-lg font-content text-foreground">
          {course.description}
        </p>

        {/* Modules preview */}
        <div className="space-y-4 mb-fluid-lg">
          <h2 className="text-fluid-2xl tracking-tight mb-fluid-md font-heading">
            Программа курса
          </h2>
          {Array.from({ length: Math.min(course.modules, 5) }).map((_, i) => (
            <div
              key={i}
              className="p-fluid-sm rounded-xl border-2"
              style={{
                borderColor:
                  i < (course.progress / 100) * course.modules
                    ? "var(--school)"
                    : "var(--border)",
              }}
            >
              <div className="flex items-center justify-between">
                <span className="text-fluid-base font-content">
                  Модуль {i + 1}
                </span>
                {i < (course.progress / 100) * course.modules && (
                  <span className="text-fluid-xs tracking-wide px-3 py-1 rounded-full font-content text-white bg-school">
                    Пройдено
                  </span>
                )}
              </div>
            </div>
          ))}
          {course.modules > 5 && (
            <p className="text-fluid-sm text-center font-content text-muted">
              И ещё {course.modules - 5} модулей
            </p>
          )}
        </div>

        {/* CTA */}
        <button className="w-full px-8 py-4 rounded-full text-fluid-base transition-all font-content bg-school text-white">
          {isLoggedIn && course.progress > 0
            ? "Продолжить обучение"
            : "Начать курс"}
        </button>
      </div>
    </div>
  );
}

export function ProfilePage({
  courses,
  onBack,
  onLogout,
}: {
  courses: Course[];
  onBack: () => void;
  onLogout: () => void;
}) {
  const completedCourses = courses.filter((c) => c.progress === 100);
  const inProgressCourses = courses.filter(
    (c) => c.progress > 0 && c.progress < 100,
  );
  const totalProgress = Math.round(
    courses.reduce((sum, c) => sum + c.progress, 0) / courses.length,
  );

  return (
    <div className="px-fluid-lg py-fluid-xl">
      {/* Back button */}
      <button
        onClick={onBack}
        className="mb-fluid-md text-fluid-base transition-colors flex items-center gap-fluid-xs"
        style={{ fontFamily: "var(--font-content)", color: "var(--school)" }}
      >
        <span>←</span> Назад
      </button>

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-fluid-lg">
          <div>
            <h1 className="text-fluid-4xl tracking-tight mb-fluid-xs font-heading leading-tight">
              Ваш профиль
            </h1>
            <p className="text-fluid-lg font-content text-muted">
              student@practicum.group
            </p>
          </div>
          <button
            onClick={onLogout}
            className="px-5 py-2.5 rounded-full text-fluid-sm transition-all font-content text-muted border-2 border-border"
          >
            Выйти
          </button>
        </div>

        {/* Stats */}
        <div className="grid lg:grid-cols-3 gap-fluid-md mb-fluid-lg">
          <div className="p-fluid-md rounded-xl border-2 border-school">
            <span className="text-fluid-4xl block mb-fluid-xs font-heading text-school">
              {totalProgress}%
            </span>
            <span className="text-fluid-sm font-content text-foreground">
              Общий прогресс
            </span>
          </div>

          <div className="p-fluid-md rounded-xl border-2 border-border">
            <span className="text-fluid-4xl block mb-fluid-xs font-heading text-school">
              {completedCourses.length}
            </span>
            <span className="text-fluid-sm font-content text-foreground">
              Завершённых курсов
            </span>
          </div>

          <div className="p-fluid-md rounded-xl border-2 border-border">
            <span className="text-fluid-4xl block mb-fluid-xs font-heading text-school">
              {inProgressCourses.length}
            </span>
            <span className="text-fluid-sm font-content text-foreground">
              В процессе
            </span>
          </div>
        </div>

        {/* Completed courses */}
        {completedCourses.length > 0 && (
          <div className="mb-fluid-lg">
            <h2 className="text-fluid-2xl tracking-tight mb-fluid-md font-heading">
              Завершённые курсы
            </h2>
            <div className="space-y-4">
              {completedCourses.map((course) => (
                <div
                  key={course.id}
                  className="flex items-center justify-between p-fluid-sm rounded-xl border-2 border-school"
                >
                  <div>
                    <h3 className="text-fluid-lg mb-fluid-xs font-heading">
                      {course.title}
                    </h3>
                    <DifficultyStars level={course.difficulty} />
                  </div>
                  <AccentStar color="var(--school)" size={16} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* In progress */}
        {inProgressCourses.length > 0 && (
          <div>
            <h2 className="text-fluid-2xl tracking-tight mb-fluid-md font-heading">
              В процессе
            </h2>
            <div className="space-y-4">
              {inProgressCourses.map((course) => (
                <div
                  key={course.id}
                  className="p-fluid-sm rounded-xl border-2 border-border"
                >
                  <div className="flex items-start justify-between mb-fluid-xs">
                    <h3 className="text-fluid-lg font-heading">
                      {course.title}
                    </h3>
                    {course.favorite && (
                      <AccentStar color="var(--school)" size={14} />
                    )}
                  </div>
                  <ProgressBar progress={course.progress} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
